import React from 'react';
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
  useRouteLoaderData,
  useSubmit
} from 'react-router-dom';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  CalendarIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  UserIcon,
  TicketIcon,
  CheckIcon,
  BuildingOfficeIcon,
  TagIcon,
  UserCircleIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { PageTitle } from '@oysterjs/uiv2/page';
import { Badge } from '@oysterjs/uiv2/badge';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '@oysterjs/uiv2/table';
import { Input, InputGroup } from '@oysterjs/uiv2/input';
import { Button } from '@oysterjs/uiv2/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/uiv2/fieldset';
import { Select } from '@oysterjs/uiv2/select';
import { Await } from '@oysterjs/uiv2/async';
import { PageNavigation } from '@oysterjs/uiv2/navigation';
import {
  AddServiceRequestCommentInput,
  AgencyUserFieldsFragment,
  CallTranscriptFieldsFragment,
  CallTranscriptRole,
  CaraWorkspaceMessageInput,
  CreateCaraWorkspaceMutation,
  CreateCaraWorkspaceDocument,
  EmailSenderRole,
  EmailThreadConversationFieldsFragment,
  ResolveServiceRequestInput,
  ResolveServiceRequestMutation,
  ServiceRequestCallFieldsFragment,
  ServiceRequestFieldsFragment,
  ServiceRequestState,
  ServiceRequestType,
  ServiceRequestUpdateType,
  UpdateInitiatorType,
  UpdateServiceRequestInput,
  UpdateServiceRequestMutation,
  CaraWorkspacePreset,
  DocumentType
} from '../api/graphql';
import { Spinner } from '@oysterjs/uiv2/spinner';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/uiv2/dialog';
import { MultiListbox, MultiListboxOption } from '@oysterjs/uiv2/multiselect';
import { useFormActions } from '@oysterjs/corev2/graphql/form';
import { ValidationError } from '@oysterjs/corev2/graphql';
import { Textarea } from '@oysterjs/uiv2/textarea';
import { FileInput } from '@oysterjs/uiv2/file';
import { client } from '../api/client';
import { ActionResult } from '@oysterjs/corev2/graphql/actions';
import { getDisplayInsuranceType } from '../api/map';
import { ApolloError } from '@apollo/client';
import { Combobox } from '@oysterjs/uiv2/combobox';

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const getAssignee = (serviceRequest: ServiceRequestFieldsFragment) => {
  if (serviceRequest.assignee) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="rounded-full size-7 bg-blue-300/50 px-3 py-1 cursor-default text-xs font-medium inline-flex flex-row items-center justify-center gap-1"
          aria-hidden
        >
          {serviceRequest.assignee.name.split(' ').at(0)?.charAt(0)}
          {serviceRequest.assignee.name.split(' ').at(1)?.charAt(0)}
        </div>
        <span>{serviceRequest.assignee.name}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="text-sm text-neutral-500">&mdash;</div>
    </div>
  );
};

const searchJson = (s: ServiceRequestFieldsFragment, search: string) => {
  const values = [s.account?.account.name || '', s.state, s.type, s.assignee?.name || ''];
  return values.some((v) => v?.toString()?.toLowerCase().indexOf(search.toLowerCase()) !== -1);
};

export const ServicingPage = () => {
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<ServiceRequestState | null>(ServiceRequestState.Open);
  const [assignee] = React.useState<string | null>(null);

  const loader = useLoaderData() as
    | { data: Promise<{ serviceRequests?: ServiceRequestFieldsFragment[] }> }
    | undefined;

  return (
    <PageTitle title="Servicing" description="Service requests from your customers">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between">
            <form className="w-full">
              <Fieldset>
                <FieldGroup>
                  <div className="flex gap-4">
                    <Field className="w-full max-w-md">
                      <Label className="sr-only" data-slot="none">
                        Filter table
                      </Label>
                      <InputGroup>
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        <Input
                          type="search"
                          name="search"
                          placeholder="Start typing to filter..."
                          onChange={(e) => setSearch(e.currentTarget.value)}
                        />
                      </InputGroup>
                    </Field>
                    <Field>
                      <Label className="sr-only" data-slot="none">
                        Filter by status
                      </Label>
                      <Select
                        name="status"
                        value={status || ''}
                        onChange={(e) => setStatus(e.currentTarget.value as ServiceRequestState)}
                      >
                        <option value="" disabled>
                          Filter by status
                        </option>
                        <option value={ServiceRequestState.Open}>Open</option>
                        <option value={ServiceRequestState.Closed}>Closed</option>
                        <option value="">All</option>
                      </Select>
                    </Field>
                  </div>
                </FieldGroup>
              </Fieldset>
            </form>
          </div>
        </div>

        <React.Suspense fallback={<ServiceRequestsPageLoader />}>
          <Await resolve={loader?.data}>
            {({ serviceRequests }) => (
              <div className="px-4 sm:px-6 rounded-lg bg-white shadow">
                {!serviceRequests?.length && (
                  <div className="text-center py-12">
                    <TicketIcon className="mx-auto h-12 w-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900">
                      No service requests found
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500">
                      Your clients haven't made any service requests yet.
                    </p>
                  </div>
                )}
                {!!serviceRequests?.length && (
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        <TableHeader className="py-4">Customer</TableHeader>
                        <TableHeader className="py-4">Status</TableHeader>
                        <TableHeader className="py-4">Request Type</TableHeader>
                        <TableHeader className="py-4">Assignee</TableHeader>
                        <TableHeader className="py-4">Due Date</TableHeader>
                        <TableHeader className="py-4">Opened At</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serviceRequests
                        .filter(
                          (s) =>
                            (!search || searchJson(s, search)) &&
                            (!status || s.state === status) &&
                            (!assignee || s.assignee?.id === assignee)
                        )
                        .map((serviceRequest) => (
                          <TableRow
                            key={serviceRequest.id}
                            href={`/app/servicing/${serviceRequest.id}/overview`}
                          >
                            <TableCell>
                              {serviceRequest.account?.account.name && (
                                <div className="font-medium flex gap-1 items-center">
                                  <div>{serviceRequest.account.account.name} </div>
                                  <div>
                                    <CheckBadgeIcon className="size-4 text-blue-400" />
                                  </div>
                                </div>
                              )}
                              {!serviceRequest.account?.account.name &&
                                !!serviceRequest.clientName && (
                                  <div className="font-medium">{serviceRequest.clientName}</div>
                                )}

                              {!serviceRequest.account?.account.name &&
                                !serviceRequest.clientName &&
                                !!serviceRequest.clientPhone && (
                                  <div className="font-medium">{serviceRequest.clientPhone}</div>
                                )}
                              {!serviceRequest.account?.account.name &&
                                !serviceRequest.clientName &&
                                !serviceRequest.clientPhone && (
                                  <div className="font-medium text-neutral-500">—</div>
                                )}
                            </TableCell>
                            <TableCell>
                              <Badge
                                // eslint-disable-next-line
                              // @ts-ignore
                                color={
                                  {
                                    [ServiceRequestState.Open]: 'amber',
                                    [ServiceRequestState.Closed]: 'green'
                                  }[serviceRequest.state] || 'neutral'
                                }
                              >
                                {serviceRequest.state}
                              </Badge>
                            </TableCell>
                            <TableCell>{titleCase(serviceRequest.type)}</TableCell>
                            <TableCell>{getAssignee(serviceRequest)}</TableCell>
                            <TableCell>
                              {serviceRequest.dueAt &&
                                new Intl.DateTimeFormat('en-US', {
                                  dateStyle: 'medium'
                                }).format(new Date(serviceRequest.dueAt))}
                              {!serviceRequest.dueAt && (
                                <div className="text-sm text-neutral-500">&mdash;</div>
                              )}
                            </TableCell>
                            <TableCell>
                              {serviceRequest.createdAt &&
                                new Intl.DateTimeFormat('en-US', {
                                  dateStyle: 'medium',
                                  timeStyle: 'short'
                                }).format(new Date(serviceRequest.createdAt))}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Await>
        </React.Suspense>
      </div>
    </PageTitle>
  );
};

export const ServiceRequestsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading service requests...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

export const ServiceRequestPage = () => {
  const loader = useLoaderData() as
    | {
        data: Promise<{
          serviceRequest?: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
          agencyUsers?: AgencyUserFieldsFragment[];
        }>;
      }
    | undefined;

  const [showResolveServiceRequestDialog, setShowResolveServiceRequestDialog] =
    React.useState(false);
  const [showUpdateServiceRequestDialog, setShowUpdateServiceRequestDialog] = React.useState(false);
  const [showOpenInCopilotDialog, setShowOpenInCopilotDialog] = React.useState(false);

  return (
    <>
      <React.Suspense fallback={<Spinner size="size-8" color="neutral" />}>
        <Await resolve={loader?.data}>
          {({ serviceRequest, agencyUsers }) =>
            serviceRequest &&
            agencyUsers && (
              <>
                <div className="lg:flex lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <h2 className="m-0 text-2xl font-semibold text-neutral-900 sm:truncate sm:text-3xl sm:tracking-tight">
                      Service Request
                    </h2>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
                      <div className="mt-2 flex items-center text-sm text-neutral-500">
                        <BriefcaseIcon
                          aria-hidden="true"
                          className="mr-1.5 size-5 shrink-0 text-neutral-400"
                        />
                        {serviceRequest.account?.account.name || serviceRequest.clientName || '—'}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-neutral-500">
                        <TagIcon
                          aria-hidden="true"
                          className="mr-1.5 size-5 shrink-0 text-neutral-400"
                        />
                        {titleCase(serviceRequest.type)}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-neutral-500">
                        <CalendarIcon
                          aria-hidden="true"
                          className="mr-1.5 size-5 shrink-0 text-neutral-400"
                        />
                        Created on{' '}
                        {serviceRequest.createdAt &&
                          new Intl.DateTimeFormat('en-US', {
                            dateStyle: 'medium'
                          }).format(new Date(serviceRequest.createdAt))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    {serviceRequest.state === ServiceRequestState.Open && (
                      <Button
                        type="button"
                        color="sky"
                        onClick={() => setShowResolveServiceRequestDialog(true)}
                      >
                        <CheckIcon
                          aria-hidden="true"
                          className="-ml-0.5 mr-1.5 size-5 text-white-400"
                        />
                        Resolve
                      </Button>
                    )}
                    {serviceRequest.state === ServiceRequestState.Closed && (
                      <Button type="button" color="sky" disabled>
                        <CheckIcon
                          aria-hidden="true"
                          className="-ml-0.5 mr-1.5 size-5 text-white-400"
                        />
                        Resolved
                      </Button>
                    )}

                    {/* Dropdown */}
                    <Menu as="div" className="relative ml-3">
                      <MenuButton className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 hover:ring-neutral-400">
                        More
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="-mr-1 ml-1.5 size-5 text-neutral-400"
                        />
                      </MenuButton>

                      <MenuItems
                        transition
                        className="absolute right-0 z-10 -mr-1 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                      >
                        <MenuItem>
                          <a
                            onClick={() => setShowOpenInCopilotDialog(true)}
                            className="block px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                          >
                            Open in Copilot
                          </a>
                        </MenuItem>
                        <MenuItem>
                          <a
                            className="block px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none"
                            onClick={() => setShowUpdateServiceRequestDialog(true)}
                          >
                            Update request
                          </a>
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  </div>
                </div>
                <PageNavigation
                  routes={[
                    {
                      path: 'overview',
                      title: 'Overview'
                    },
                    {
                      path: 'conversation',
                      title: 'Conversation',
                      hidden: !serviceRequest.calls?.length && !serviceRequest.emailThreads?.length
                    },
                    {
                      path: 'activity',
                      title: 'Comments and Activity'
                    }
                  ]}
                />
                <UpdateServiceRequestDialog
                  open={showUpdateServiceRequestDialog}
                  onClose={() => setShowUpdateServiceRequestDialog(false)}
                  serviceRequest={serviceRequest}
                  agencyUsers={agencyUsers}
                />
                <OpenInCopilotDialog
                  open={showOpenInCopilotDialog}
                  onClose={() => setShowOpenInCopilotDialog(false)}
                  serviceRequest={serviceRequest}
                />
              </>
            )
          }
        </Await>
      </React.Suspense>
      <ResolveServiceRequestDialogWrapper
        open={showResolveServiceRequestDialog}
        onClose={() => setShowResolveServiceRequestDialog(false)}
      />
    </>
  );
};

const ResolveServiceRequestDialogWrapper = (props: { open: boolean; onClose: () => void }) => {
  const action = useActionData() as ActionResult<ResolveServiceRequestMutation>;
  const submit = useSubmit();
  const navigation = useNavigation();
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location.pathname) {
      props.onClose();
    }
  }, [navigation.state, navigation.formAction]);

  const handleSubmit = async (form: ResolveServiceRequestInput) => {
    if (!form) {
      return;
    }

    // Construct a multi-part form data request with the file
    const formData = new FormData();
    form.files.forEach((f) => formData.append('files', f));
    formData.append('notes', form.notes);

    submit(formData, { method: 'POST', action: 'resolve', encType: 'multipart/form-data' });
  };

  return (
    <ResolveServiceRequestDialog
      open={props.open}
      onClose={props.onClose}
      loading={navigation.state === 'submitting'}
      validationErrors={validationErrors}
      onSubmit={handleSubmit}
    />
  );
};

export const ResolveServiceRequestDialog = (props: {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onSubmit: (form: ResolveServiceRequestInput) => Promise<void>;
  validationErrors: ValidationError[];
}) => {
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<ResolveServiceRequestInput>({
    files: [],
    notes: ''
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (props.validationErrors) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>Resolve Service Request</DialogTitle>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit(form);
        }}
      >
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Notes</Label>
                <Description>These notes will not be visible to the client.</Description>
                <Textarea
                  value={form.notes || ''}
                  rows={3}
                  onChange={(e) => {
                    const notes = e.currentTarget.value;
                    updateForm(['notes'], (prev) => ({ ...prev, notes }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['notes'])}
                />
                {getValidationError(validationErrors, ['notes']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['notes'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Files</Label>
                <Description>
                  Drag and drop or click to select files from your computer. You can upload multiple
                  files at once. These files will be visible to the client as part of the service
                  request resolution.
                </Description>
                <FileInput
                  multiple
                  onChange={(files) => updateForm(['files'], (prev) => ({ ...prev, files: files }))}
                  invalid={!!getValidationError(validationErrors, ['files'])}
                />
                {getValidationError(validationErrors, ['files']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['files'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={props.loading} onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={props.loading}>
            Resolve Request
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const OpenInCopilotDialog = (props: {
  open: boolean;
  onClose: () => void;
  serviceRequest: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [preset, setPreset] = React.useState<CaraWorkspacePreset | null | undefined>(
    props.serviceRequest.type === ServiceRequestType.Question
      ? CaraWorkspacePreset.DocumentExploration
      : props.serviceRequest.type === ServiceRequestType.QuoteRequest
        ? CaraWorkspacePreset.GenerateTemplate
        : props.serviceRequest.type === ServiceRequestType.CreateCertificate
          ? CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance
          : undefined
  );
  const [form, setForm] = React.useState<CaraWorkspaceMessageInput>({
    files: [],
    message: '',
    serviceRequestId: props.serviceRequest.id,
    documentIds: []
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  const updateFiles = (files: File[], label?: string) => {
    updateForm(['files'], (prev) => ({
      ...prev,
      files: files.map(
        (f) =>
          new File([f], label ? label + ' ' + f.name : f.name, {
            type: f.type,
            lastModified: f.lastModified
          })
      )
    }));
  };

  const updateMessage = (message: string) => {
    updateForm(['message'], (prev) => ({ ...prev, message }));
  };

  const updateDocumentIds = (documentIds: DocumentType[]) => {
    updateForm(['documentIds'], (prev) => ({
      ...prev,
      documentIds
    }));
  };

  const createWorkspace = async (
    query: string,
    attachments: File[],
    serviceRequestId: string,
    documentIds: DocumentType[]
  ) => {
    const res = await client.mutate<CreateCaraWorkspaceMutation>({
      mutation: CreateCaraWorkspaceDocument,
      variables: {
        input: {
          preset: preset || undefined,
          message: query.trim(),
          files: attachments,
          serviceRequestId,
          documentIds
        }
      }
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    const id = res.data?.createCaraWorkspace?.id;
    if (!id) {
      throw new Error('Failed to create workspace');
    }

    navigate(`/app/workspaces/${id}`);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();
    setLoading(true);

    try {
      await createWorkspace(
        form.message,
        form.files,
        props.serviceRequest.id,
        form.documentIds || []
      );
    } finally {
      setLoading(false);
    }
  };

  const presetOptions = [
    {
      preset: CaraWorkspacePreset.CertificateReview,
      title: 'Certificate Review',
      description: 'Review a certificate for any discrepancies and E&O exposure'
    },
    {
      preset: CaraWorkspacePreset.ContractReview,
      title: 'Contract Review',
      description:
        'Review a contract for insurance requirements and compare it to quote or policy details'
    },
    {
      preset: CaraWorkspacePreset.CoverageComparison,
      title: 'Coverage Comparison',
      description: 'Compare coverage details between multiple quotes or policies'
    },
    {
      preset: CaraWorkspacePreset.DocumentExploration,
      title: 'Explore a Policy',
      description: 'Ask Cara to answer questions about a policy document',
      suggested: props.serviceRequest.type === ServiceRequestType.Question
    },
    {
      preset: CaraWorkspacePreset.GenerateTemplate,
      title: 'Fill Template',
      description: 'Cross-fill forms and templates from other documents and instructions',
      suggested: props.serviceRequest.type === ServiceRequestType.QuoteRequest
    },
    {
      preset: CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance,
      title: 'Generate Certificate',
      description: 'Generate a certificate of liability insurance (ACORD 25) for a customer',
      suggested: props.serviceRequest.type === ServiceRequestType.CreateCertificate
    },
    {
      preset: null,
      title: 'Custom',
      description:
        'Do something else. Cara will perform better if your task fits one of the above options.',
      suggested: false
    }
  ].sort((a, b) => (a.suggested ? (b.suggested ? (a.title < b.title ? -1 : 1) : -1) : 1));

  return (
    <Dialog open={props.open} onClose={props.onClose} size="xl">
      <DialogTitle>Open in Copilot</DialogTitle>
      <DialogDescription>
        Ask Cara to help you resolve this service request. Specify what task you want Cara to do and
        include any additional instructios.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>How should Cara help?</Label>
                <Description>Choose what you want Cara to do</Description>
                <Combobox
                  value={preset === null ? 'Custom' : preset}
                  minQueryLength={0}
                  maxOptions={1000}
                  onChange={(preset) =>
                    setPreset(preset === 'Custom' ? null : (preset as CaraWorkspacePreset))
                  }
                  options={presetOptions.map((p) => ({
                    value: p.preset || 'Custom',
                    label: p.title,
                    description: p.description,
                    badgeText: p.suggested ? 'Suggested' : undefined
                  }))}
                  error={getValidationError(validationErrors, ['preset'])}
                />
              </Field>
            </FieldGroup>
            {preset === CaraWorkspacePreset.CertificateReview && (
              <FieldGroup>
                <Field>
                  <Label>Certificate of Insurance</Label>
                  <Description>
                    Upload the certificate of insurance you want Cara to review.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Certificate')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Quotes or Policies</Label>
                  <Description>
                    Upload any quotes or policies you want Cara to compare to the certificate of
                    insurance.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Quote or Policy')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
                <Field>
                  <Label>Additional Notes</Label>
                  <Description>
                    Add any additional notes you want Cara to consider when reviewing the
                    certificate of insurance.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={5}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.ContractReview && (
              <FieldGroup>
                <Field>
                  <Label>Contract</Label>
                  <Description>
                    Upload the contract you want Cara to review for insurance requirements.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Contract')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Quotes or Policies</Label>
                  <Description>
                    Upload any quotes or policies you want Cara to compare to the contract
                    requirements.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Quote or Policy')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
                <Field>
                  <Label>Additional Notes</Label>
                  <Description>
                    Add any additional notes you want Cara to consider when reviewing the contract.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={5}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.CoverageComparison && (
              <FieldGroup>
                <Field>
                  <Label>First Quote or Policy</Label>
                  <Description>
                    Upload the first quote or policy you want Cara to compare to the second quote or
                    policy.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Option 1')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Second Quote or Policy</Label>
                  <Description>
                    Upload the second quote or policy you want Cara to compare to the first quote or
                    policy.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Option 2')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <Field>
                  <Label>Additional Notes</Label>
                  <Description>
                    Add any additional notes you want Cara to consider when comparing the quotes or
                    policies.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={5}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.GenerateCertificateOfLiabilityInsurance && (
              <FieldGroup>
                <Field>
                  <Label>Binders or Policies</Label>
                  <Description>
                    Upload the binders or policies you want Cara to use while generating a
                    certificate of liability insurance.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Binder or Policy')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
                <Field>
                  <Label>Additional Information</Label>
                  <Description>
                    Add any additional information you want Cara to consider when generating a
                    certificate of liability insurance, such as the certificate holder's name and
                    address, the description of operations, or any other requirements.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={4}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.GenerateTemplate && (
              <FieldGroup>
                <Field>
                  <Label>Input Files</Label>
                  <Description>
                    Any files you'd like Cara to parse and use as input to cross-fill into
                    templates.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Input')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
                <Field>
                  <Label>Templates</Label>
                  <Description>Choose the templates you'd like Cara to fill out.</Description>
                  <MultiListbox
                    onChange={(value: DocumentType[]) => updateDocumentIds(value)}
                    value={form.documentIds}
                    placeholder="Select templates"
                  >
                    {Object.values(DocumentType)
                      .filter((t) =>
                        [
                          DocumentType.Acord_125,
                          DocumentType.Acord_126,
                          DocumentType.Acord_130,
                          DocumentType.Acord_131,
                          DocumentType.Acord_140
                        ].includes(t)
                      )
                      .map((type) => (
                        <MultiListboxOption key={type} value={type}>
                          {type.replace(/_/g, ' ')}
                        </MultiListboxOption>
                      ))}
                  </MultiListbox>
                </Field>
                <Field>
                  <Label>Additional Information</Label>
                  <Description>
                    Add any additional information you want Cara to consider when filling out the
                    template. This can be specific instructions or other free-form information that
                    should be parsed and mapped to template fields
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    rows={5}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === CaraWorkspacePreset.DocumentExploration && (
              <FieldGroup>
                <Field>
                  <Label>Input Files</Label>
                  <Description>
                    Files you'd like Cara to use as context to answer questions
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files, 'Input')}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
                <Field>
                  <Label>Question or Prompt</Label>
                  <Description>
                    Any question or prompt you'd like Cara to answer about the documents.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    rows={5}
                  />
                </Field>
              </FieldGroup>
            )}

            {preset === null && (
              <FieldGroup>
                <Field>
                  <Label>Instructions</Label>
                  <Description>
                    Any additional instructions to Cara to help resolve the service request, aside
                    from the information already captured.
                  </Description>
                  <Textarea
                    value={form.message || ''}
                    rows={5}
                    onChange={(e) => updateMessage(e.currentTarget.value)}
                    invalid={!!getValidationError(validationErrors, ['message'])}
                  />
                  {getValidationError(validationErrors, ['message']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['message'])}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Files</Label>
                  <Description>
                    Drag and drop or click to select files from your computer. You can upload
                    multiple files at once. These files will be visible to the client as part of the
                    service request resolution.
                  </Description>
                  <FileInput
                    multiple
                    onChange={(files) => updateFiles(files)}
                    invalid={!!getValidationError(validationErrors, ['files'])}
                  />
                  {getValidationError(validationErrors, ['files']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['files'])}</ErrorMessage>
                  )}
                </Field>
                <AlreadyIncludedFiles serviceRequest={props.serviceRequest} />
              </FieldGroup>
            )}
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={loading} disabled={preset === undefined}>
            Open in Copilot
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const AlreadyIncludedFiles = (props: {
  serviceRequest: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
}) => {
  const files = props.serviceRequest.emailThreads?.flatMap((thread) => thread.attachments);
  if (!files?.length) {
    return null;
  }

  return (
    <Field>
      <Label>Already Included Files</Label>
      <Description>
        These files will automatically be included in the Copilot workspace and do not need to be
        uploaded again.
      </Description>
      <div data-slot="control" className="flex flex-wrap flex-row gap-2">
        {files.map((file) => (
          <Link
            key={file.id}
            to={file.url || ''}
            target="_blank"
            className="rounded-full border border-neutral-200 bg-white px-3 py-1 cursor-default flex flex-row items-center justify-center gap-1 hover:shadow hover:cursor-pointer transition-all duration-150"
          >
            <PaperClipIcon className="size-3 text-neutral-500" />
            <p className="text-sm text-neutral-500">{file.name}</p>
          </Link>
        ))}
      </div>
    </Field>
  );
};

const UpdateServiceRequestDialog = (props: {
  open: boolean;
  onClose: () => void;
  serviceRequest: ServiceRequestFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
}) => {
  const action = useActionData() as ActionResult<UpdateServiceRequestMutation>;
  const submit = useSubmit();
  const navigation = useNavigation();

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<UpdateServiceRequestInput>({
    assigneeId: null,
    dueAt: null
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location.pathname) {
      props.onClose();
    }
  }, [navigation.state, navigation.formAction]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();

    submit(
      {
        assigneeId: form.assigneeId || null,
        dueAt: form.dueAt?.toISOString() || null
      },
      { method: 'POST', action: 'update', encType: 'application/json' }
    );
  };

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>Update Service Request</DialogTitle>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Assignee</Label>
                <Description>
                  The team member that will be assigned to this service request.
                </Description>
                <Select
                  value={form.assigneeId || ''}
                  onChange={(e) => {
                    const assigneeId = e.currentTarget.value;
                    updateForm(['assigneeId'], (prev) => ({ ...prev, assigneeId }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['assigneeId'])}
                >
                  <option value="">Unassigned</option>
                  {props.agencyUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
                {getValidationError(validationErrors, ['notes']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['notes'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Due Date</Label>
                <Description>The date by which the service request must be resolved.</Description>
                <Input
                  type="date"
                  value={form.dueAt ? new Date(form.dueAt).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const dueAt = e.currentTarget.value;
                    updateForm(['dueAt'], (prev) => ({
                      ...prev,
                      dueAt: new Date(dueAt)
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['dueAt'])}
                />
                {getValidationError(validationErrors, ['dueAt']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['dueAt'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={navigation.state === 'submitting'}>
            Update Request
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export const ServiceRequestOverviewPage = () => {
  const loader = useRouteLoaderData('servicing/:id') as
    | {
        data: Promise<{
          serviceRequest?: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {({ serviceRequest }) =>
          serviceRequest && (
            <div className="flex gap-8">
              <ServiceRequestDetailsPanel serviceRequest={serviceRequest} />
            </div>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const ServiceRequestDetailsPanel = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  return (
    <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow flex-grow flex-shrink-0">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
          <div>
            <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900">
              {titleCase(props.serviceRequest.type)}
            </h3>
            <p className="text-sm text-neutral-500">Details about this service request.</p>
          </div>
        </div>
      </div>
      <div className="py-2">
        {props.serviceRequest.type === ServiceRequestType.AddAdditionalInsured && (
          <ServiceRequestAddAdditionalInsuredDetails serviceRequest={props.serviceRequest} />
        )}
        {props.serviceRequest.type === ServiceRequestType.CancelPolicy && (
          <ServiceRequestCancelPolicyDetails serviceRequest={props.serviceRequest} />
        )}
        {props.serviceRequest.type === ServiceRequestType.CreateCertificate && (
          <ServiceRequestCreateCertificateDetails serviceRequest={props.serviceRequest} />
        )}
        {props.serviceRequest.type === ServiceRequestType.DocumentRequest && (
          <ServiceRequestDocumentRequestDetails serviceRequest={props.serviceRequest} />
        )}
        {props.serviceRequest.type === ServiceRequestType.EndorsePolicy && (
          <ServiceRequestEndorsePolicyDetails serviceRequest={props.serviceRequest} />
        )}
        {(props.serviceRequest.type === ServiceRequestType.Other ||
          props.serviceRequest.type === ServiceRequestType.QuoteRequest) && (
          <ServiceRequestOtherDetails serviceRequest={props.serviceRequest} />
        )}
        {props.serviceRequest.type === ServiceRequestType.Question && (
          <ServiceRequestQuestionDetails serviceRequest={props.serviceRequest} />
        )}
      </div>
    </div>
  );
};

const ServiceRequestCommonDetails = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  return (
    <>
      {props.serviceRequest.clientName && (
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Client Name</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.clientName}
          </dd>
        </div>
      )}
      {props.serviceRequest.clientEmail && (
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Client Email</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.clientEmail}
          </dd>
        </div>
      )}
      {props.serviceRequest.clientPhone && (
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Client Phone</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.clientPhone}
          </dd>
        </div>
      )}
    </>
  );
};

const ServiceRequestAddAdditionalInsuredDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container flex-grow flex-shrink-0">
      <dl className="divide-y divide-neutral-100">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Additional Insured</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.addAdditionalInsured?.name}
          </dd>
        </div>
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Address</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.addAdditionalInsured?.address?.line1}
            <br />
            {props.serviceRequest.addAdditionalInsured?.address?.city},{' '}
            {props.serviceRequest.addAdditionalInsured?.address?.zone}{' '}
            {props.serviceRequest.addAdditionalInsured?.address?.postalCode}
          </dd>
        </div>
        {props.serviceRequest.addAdditionalInsured?.relationship && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Relationship</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {props.serviceRequest.addAdditionalInsured?.relationship}
            </dd>
          </div>
        )}
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Additional Notes</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestCancelPolicyDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Cancellation Details</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.cancelPolicy?.reason}
          </dd>
        </div>
      </dl>
    </div>
  );
};

const ServiceRequestCreateCertificateDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Certificate Holder</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.createCertificate?.name}
          </dd>
        </div>
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Address</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.createCertificate?.address?.line1}
            <br />
            {props.serviceRequest.createCertificate?.address?.city},{' '}
            {props.serviceRequest.createCertificate?.address?.zone}{' '}
            {props.serviceRequest.createCertificate?.address?.postalCode}
          </dd>
        </div>
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Additional Insured</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.createCertificate?.additionalInsured ? 'Yes' : 'No'}
          </dd>
        </div>
        {props.serviceRequest.createCertificate?.additionalInsured && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Relationship</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {props.serviceRequest.createCertificate?.additionalInsuredRelationship}
              {props.serviceRequest.createCertificate?.additionalInsuredRelationshipDescription}
            </dd>
          </div>
        )}
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Additional Notes</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestDocumentRequestDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Document Type</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {titleCase(props.serviceRequest.documentRequest?.type || '')}
          </dd>
        </div>
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Request Details</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestEndorsePolicyDetails = (props: {
  serviceRequest: ServiceRequestFieldsFragment;
}) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Endorsement Details</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestOtherDetails = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Request Details</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              <pre className="whitespace-break-spaces font-body">
                {props.serviceRequest.details}
              </pre>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const ServiceRequestQuestionDetails = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  return (
    <div className="@container">
      <dl className="divide-y divide-neutral-100">
        <ServiceRequestCommonDetails serviceRequest={props.serviceRequest} />
        {props.serviceRequest.businessPolicy?.id && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Policy</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {getDisplayInsuranceType(props.serviceRequest.businessPolicy?.type)} (
              {props.serviceRequest.businessPolicy?.carrier?.name})
            </dd>
          </div>
        )}
        <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
          <dt className="text-sm font-medium text-neutral-500">Question</dt>
          <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
            {props.serviceRequest.question?.question}
          </dd>
        </div>
        {props.serviceRequest.details && (
          <div className="px-4 sm:px-6 py-3 flex flex-col gap-0.5">
            <dt className="text-sm font-medium text-neutral-500">Endorsement Details</dt>
            <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
              {props.serviceRequest.details}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

export const ServiceRequestConversationPage = () => {
  const loader = useRouteLoaderData('servicing/:id') as
    | {
        data: Promise<{
          serviceRequest?: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {({ serviceRequest }) =>
          serviceRequest && (
            <>
              <div className="flex flex-col space-y-4 p-4">
                {parseConversation(serviceRequest.calls, serviceRequest.emailThreads).map(
                  (message) =>
                    [
                      EmailSenderRole.Client,
                      EmailSenderRole.Participant,
                      EmailSenderRole.Agent
                    ].includes(message.role) ? (
                      <div className="flex items-start space-x-4 max-w-xl">
                        <div className="flex-shrink-0">
                          <div className="size-10 rounded-full bg-neutral-200 flex items-center justify-center">
                            <UserIcon className="size-6 text-neutral-500" />
                          </div>
                        </div>
                        <div className="flex-1 bg-neutral-100 rounded-lg p-4">
                          <p className="text-sm text-neutral-900">{message.content}</p>
                          {/* <span className="text-xs text-neutral-500 mt-1 block">10:23 AM</span> */}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start space-x-4 max-w-xl self-end">
                        <div className="flex-1 bg-sky-100 rounded-lg p-4">
                          <p className="text-sm text-neutral-900">{message.content}</p>
                          {/* <span className="text-xs text-neutral-500 mt-1 block">10:24 AM</span> */}
                        </div>
                        <div className="flex-shrink-0">
                          <div className="size-10 rounded-full bg-sky-200 flex items-center justify-center">
                            <BuildingOfficeIcon className="size-6 text-sky-600" />
                          </div>
                        </div>
                      </div>
                    )
                )}
              </div>
            </>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const parseConversation = (
  calls: CallTranscriptFieldsFragment[],
  emailThreads: EmailThreadConversationFieldsFragment[]
) => {
  if (calls?.at(-1)) {
    return (
      calls
        .at(-1)
        ?.transcript.filter(
          (t) => t.role === CallTranscriptRole.Agent || t.role === CallTranscriptRole.User
        )
        .filter((t) => !!t.content)
        .map((t) => ({
          role: t.role === CallTranscriptRole.Agent ? EmailSenderRole.Cara : EmailSenderRole.Client,
          content: t.content
        })) || []
    );
  }
  if (emailThreads?.[0]) {
    return [...emailThreads[0].caraEmails, ...emailThreads[0].participantEmails]
      .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime())
      .map((e) => ({ role: e.senderRole, content: e.body }));
  }
  return [];
};

export const ServiceRequestCommentsAndActivityPage = () => {
  const loader = useRouteLoaderData('servicing/:id') as
    | {
        data: Promise<{
          serviceRequest?: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
          agencyUsers: AgencyUserFieldsFragment[];
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {({ serviceRequest, agencyUsers }) =>
          serviceRequest &&
          agencyUsers && (
            <CommentsAndActivityPage serviceRequest={serviceRequest} agencyUsers={agencyUsers} />
          )
        }
      </Await>
    </React.Suspense>
  );
};

const CommentsAndActivityPage = (props: {
  serviceRequest: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
}) => {
  return (
    <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow flex-grow flex-shrink-0">
      <div className="px-4 py-5 sm:px-6">
        <CommentsAndActivity
          serviceRequest={props.serviceRequest}
          agencyUsers={props.agencyUsers}
        />
      </div>
    </div>
  );
};

const relativeDate = (date: Date) => {
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  const now = new Date();
  const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);

  const units = [
    { unit: 'year', seconds: 31536000 },
    { unit: 'month', seconds: 2592000 },
    { unit: 'day', seconds: 86400 },
    { unit: 'hour', seconds: 3600 },
    { unit: 'minute', seconds: 60 },
    { unit: 'second', seconds: 1 }
  ];

  for (const { unit, seconds } of units) {
    if (Math.abs(diffInSeconds) >= seconds) {
      return rtf.format(Math.round(diffInSeconds / seconds), unit as Intl.RelativeTimeFormatUnit);
    }
  }

  return rtf.format(0, 'second');
};

type ActivityItem = {
  id: string;
  type: string;
  person: string;
  date: string;
  comment: string | undefined;
  assigned: string | undefined;
  dueDate: string | undefined;
};

const CommentsAndActivity = (props: {
  serviceRequest: ServiceRequestFieldsFragment & ServiceRequestCallFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
}) => {
  const submit = useSubmit();
  const navigation = useNavigation();

  const [, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<AddServiceRequestCommentInput>({
    content: ''
  });
  const { updateForm } = useFormActions(setForm, setValidationErrors);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form?.content) {
      return;
    }

    e.preventDefault();

    submit(form, { method: 'POST', action: 'comment', encType: 'application/json' });
    setForm({ content: '' });
  };

  const agencyUserMap = Object.fromEntries(props.agencyUsers.map((u) => [u.id, u]));
  const activity = [
    ...props.serviceRequest.comments,
    ...(props.serviceRequest.versionHistory?.filter((v) =>
      v.updateTypes.some((u) =>
        [
          ServiceRequestUpdateType.AssignServiceRequest,
          ServiceRequestUpdateType.CloseServiceRequest,
          ServiceRequestUpdateType.UpdateServiceRequestDueAt
        ].includes(u)
      )
    ) || [])
  ]
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .flatMap((a) => {
      if (a.__typename === 'ServiceRequestComment') {
        return {
          id: a.id,
          type: 'comment',
          person: a.agencyUser.name,
          comment: a.content,
          date: relativeDate(new Date(a.createdAt)),
          assigned: undefined,
          dueDate: undefined
        } as ActivityItem;
      }
      if (a.__typename === 'ServiceRequestVersion') {
        return a.updateTypes.map((u) => {
          switch (u) {
            case ServiceRequestUpdateType.AssignServiceRequest:
              return {
                id: a.version.toString(),
                type: 'assignment',
                person: agencyUserMap[a.updateInitiatorId]?.name || 'unknown',
                assigned:
                  a.serviceRequest.assignee?.id === a.updateInitiatorId
                    ? 'themselves'
                    : a.serviceRequest.assignee?.name || 'no one',
                dueDate: undefined,
                date: relativeDate(new Date(a.createdAt))
              } as ActivityItem;

            case ServiceRequestUpdateType.CloseServiceRequest:
              return {
                id: a.version.toString(),
                type: 'resolved',
                person:
                  a.updateInitiatorType === UpdateInitiatorType.AgencyUser
                    ? agencyUserMap[a.updateInitiatorId]?.name || 'unknown'
                    : 'Cara',
                comment: a.serviceRequest.resolution?.notes,
                assigned: undefined,
                dueDate: undefined,
                date: relativeDate(new Date(a.createdAt))
              } as ActivityItem;

            case ServiceRequestUpdateType.UpdateServiceRequestDueAt:
              return {
                id: a.version.toString(),
                type: 'set due date',
                person: agencyUserMap[a.updateInitiatorId]?.name || 'unknown',
                assigned: undefined,
                dueDate: a.serviceRequest.dueAt
                  ? new Date(a.serviceRequest.dueAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })
                  : 'none',
                date: relativeDate(new Date(a.createdAt))
              } as ActivityItem;
          }
        });
      }

      return null;
    })
    .filter((v): v is ActivityItem => !!v);

  return (
    <div>
      <ul role="list">
        {activity.map((activityItem, activityItemIdx) => (
          <li key={activityItem.id}>
            <div className="relative pb-8">
              {activityItemIdx !== activity.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="absolute left-5 top-5 -ml-px h-full w-0.5 bg-neutral-200"
                />
              ) : null}
              <div className="relative flex items-start space-x-3">
                {activityItem.type === 'comment' ? (
                  <>
                    <div className="relative">
                      <div className="flex size-10 items-center justify-center rounded-full text-sm font-medium bg-primary-100 ring-8 ring-white">
                        {activityItem.person.charAt(0)}
                        {activityItem.person.split(' ')[1].charAt(0)}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div>
                        <div className="text-sm">
                          <span className="font-medium text-neutral-900">
                            {activityItem.person}
                          </span>
                        </div>
                        <p className="mt-0.5 text-sm text-neutral-500">
                          Commented {activityItem.date}
                        </p>
                      </div>
                      <div className="mt-2 text-sm text-neutral-700">{activityItem.comment}</div>
                    </div>
                  </>
                ) : activityItem.type === 'assignment' ? (
                  <>
                    <div>
                      <div className="relative px-1">
                        <div className="flex size-8 items-center justify-center rounded-full bg-neutral-100 ring-8 ring-white">
                          <UserCircleIcon aria-hidden="true" className="size-5 text-neutral-500" />
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1.5">
                      <div className="text-sm text-neutral-500">
                        <span className="font-medium text-neutral-900">{activityItem.person}</span>{' '}
                        assigned{' '}
                        <span className="font-medium text-neutral-900">
                          {activityItem.assigned}
                        </span>{' '}
                        <span className="whitespace-nowrap">{activityItem.date}</span>
                      </div>
                    </div>
                  </>
                ) : activityItem.type === 'resolved' ? (
                  <>
                    <div>
                      <div className="relative px-1">
                        <div className="flex size-8 items-center justify-center rounded-full bg-primary-500 ring-8 ring-white">
                          <CheckIcon aria-hidden="true" className="size-5 text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1.5">
                      <div className="text-sm text-neutral-500">
                        <span className="font-medium text-neutral-900">{activityItem.person}</span>{' '}
                        resolved{' '}
                        <span className="font-medium text-neutral-900">
                          {activityItem.assigned}
                        </span>{' '}
                        <span className="whitespace-nowrap">{activityItem.date}</span>
                      </div>
                      <div className="mt-2 text-sm text-neutral-700">{activityItem.comment}</div>
                    </div>
                  </>
                ) : activityItem.type === 'set due date' ? (
                  <>
                    <div>
                      <div className="relative px-1">
                        <div className="flex size-8 items-center justify-center rounded-full bg-neutral-100 ring-8 ring-white">
                          <CalendarIcon aria-hidden="true" className="size-5 text-neutral-500" />
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1.5">
                      <div className="text-sm text-neutral-500">
                        <span className="font-medium text-neutral-900">{activityItem.person}</span>{' '}
                        set the due date to{' '}
                        <span className="font-medium text-neutral-900">{activityItem.dueDate}</span>{' '}
                        <span className="whitespace-nowrap">{activityItem.date}</span>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex items-start space-x-4">
        <div className="shrink-0">
          <div className="flex size-10 p-2.5 items-center justify-center rounded-full text-sm font-medium bg-primary-100 ring-8 ring-white">
            <UserIcon />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <Form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="comment" className="sr-only">
                Add your comment
              </label>
              <Textarea
                rows={3}
                placeholder="Add your comment..."
                value={form.content}
                onChange={(e) => {
                  const content = e.currentTarget.value;
                  updateForm(['content'], (prev) => ({ ...prev, content }));
                }}
              />
            </div>

            <Button
              type="submit"
              color="sky"
              loading={navigation.state === 'submitting'}
              disabled={form.content.trim().length === 0}
            >
              Comment
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
};
